import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { GrooveSurfaceDirective } from '@groove/angular';

interface PricingLinkTarget {
  readonly path: string;
  readonly fragment: string | undefined;
}

/**
 * Rates + payment methods + a policies pointer, as two `.pricing-tier` cards
 * (the site's original hand-rolled version of this treatment lived at the
 * retired /bass-lessons/pricing route, folded into the home hub 2026-09) —
 * name, price row, and one "what's best for you" note per tier.
 * `policiesHref` may carry a `#fragment` (e.g. `/#policies`) — split here
 * rather than asking callers to pass path and fragment separately.
 *
 * Refinement pass (2026-09-07 night, finding #5): the `table` layout and the
 * per-card call/email actions are retired — the mid-page CTA is the action,
 * and two clear cards read better on wide screens than a table wastes space.
 * The bullet-point lists became one plain-language note per tier
 * (`hourlyNote` / `prepaidNote`) instead, in Russ's own words about who each
 * rate is for.
 *
 * Round two (2026-09-07, finding #6): the optional `savingsLine` used to
 * render as its own paragraph below the prepaid card's price, which made the
 * two cards' rows uneven. It now renders inline inside the price row itself
 * (amount, unit, then the savings, muted) so both cards carry the same three
 * rows — name, price row, note.
 */
@Component({
  selector: 'app-pricing-block',
  imports: [RouterLink, GrooveSurfaceDirective],
  templateUrl: './pricing-block.component.html',
  styleUrl: './pricing-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingBlockComponent {
  readonly hourly = input.required<string>();
  readonly prepaid = input.required<string>();
  readonly savingsLine = input<string>();
  readonly paymentMethods = input.required<readonly string[]>();
  /**
   * Optional since round three (spec §13.1 item 7): the hub dropped the link
   * because its policies disclosure sits a few lines lower in the same band.
   * When absent, no `.pricing-block-policies` paragraph renders at all.
   */
  readonly policiesHref = input<string | undefined>();
  /** One plain-language line on who the hourly rate is best for. */
  readonly hourlyNote = input.required<string>();
  /** One plain-language line on who the prepaid rate is best for. */
  readonly prepaidNote = input.required<string>();

  protected readonly policiesLink = computed<PricingLinkTarget | undefined>(
    () => {
      const href = this.policiesHref();
      if (href === undefined) return undefined;
      const [path, fragment] = href.split('#');
      return { path: path ?? href, fragment };
    },
  );
}
